<template>
  <BaseModal
    unclosable
    noPadding
    useForm
    verticalAlignCenter
    @submit="submit"
  >
    <div
      :class="[$style.base, {[$style.smallScreen]: $screen === 's', [$style.mediumScreen]: $screen === 'm', [$style.largeScreen]: $screen === 'l'}]"
    >
      <Flex
        direction="column"
        :class="$style.left"
      >
        <div>
          <BaseHeading
            size="xl"
            v-test="'wavy-onboarding-heading'"
          >
            {{ $t('wavy_onboarding.heading_1', { name: company?.name || '' }) }}
          </BaseHeading>
          <BaseHeading size="xl">
            {{ $t('wavy_onboarding.heading_2', brandName()) }}
          </BaseHeading>
        </div>
        <BaseText
          >{{ $t('wavy_onboarding.description', brandName()) }}</BaseText
        >
        <BaseText bold>{{ $t('wavy_onboarding.steps.heading') }}</BaseText>
        <div>
          <Flex>
            <div :class="$style.step">1.</div>
            <BaseText>{{ $t('wavy_onboarding.steps.step_1') }}</BaseText>
          </Flex>
          <Flex>
            <div :class="$style.step">2.</div>
            <BaseText>{{ $t('wavy_onboarding.steps.step_2') }}</BaseText>
          </Flex>
          <Flex>
            <div :class="$style.step">3.</div>
            <BaseText>{{ $t('wavy_onboarding.steps.step_3') }}</BaseText>
          </Flex>
        </div>
        <BaseText>{{ $t('wavy_onboarding.description_2') }}</BaseText>
      </Flex>
      <Flex
        direction="column"
        :class="$style.right"
      >
        <BaseText bold>{{ $t('wavy_onboarding.heading_form') }}</BaseText>
        <BaseInput
          v-model="email"
          :label="$t('global.email')"
          type="email"
          disabled
          v-test="'wavy-onboarding-email'"
        />
        <BaseInput
          v-model="password"
          :label="$t('global.password')"
          type="password"
          required
          newPassword
          v-test="'wavy-onboarding-password'"
        />
        <BaseInput
          v-model="passwordConfirmation"
          :label="$t('global.password_confirmation')"
          type="password"
          :sameAs="password"
          required
          v-test="'wavy-onboarding-password-confirmation'"
        />
        <BaseButton
          submitForm
          :loading="loading"
          v-test="'wavy-onboarding-submit'"
        >
          {{ $t('wavy_onboarding.button_text') }}
        </BaseButton>
      </Flex>
    </div>
  </BaseModal>
</template>

<script
  lang="ts"
  setup
>
import { useMutation } from '@vue/apollo-composable';
import { brandName } from '@/helpers/brand-name';
import { useCompanyStore } from '@/stores/company';
import { useUserStore } from '@/stores/user';
import { UPDATE_USER_MUTATION } from './graphql';

const { user } = useUserStore();
const { company } = useCompanyStore();
const email = ref(user?.email || '');
const password = ref('');
const passwordConfirmation = ref('');

const emit = defineEmits(['submit']);

const { mutate, loading } = useMutation(UPDATE_USER_MUTATION);

const submit = () => {
  mutate({
    input: {
      id: user?.id,
      password: password.value,
      passwordConfirmation: passwordConfirmation.value,
    },
  }).then(() => {
    if (user) {
      user.mustSetPassword = false;
    }
    emit('submit');
  });
};
</script>

<style
  lang="scss"
  module
>

.base {
  display: flex;
  border-radius: $modal-radius;
  overflow: hidden;

  &.smallScreen {
    flex-direction: column;
  }
}

.left, .right {
  .base.largeScreen & {
    padding: $spacing * 3;
  }

  .base.mediumScreen & {
    padding: $spacing * 2;
    width: 50%;
  }

  .base.smallScreen & {
    padding: $spacing * 1.5;
  }
}

.left {
  background-color: var(--color-primary);
  color: var(--color-text-inverted);

  .base.largeScreen & {
    width: 60%;
  }
}

.right {
  background-color: var(--color-bg-secondary);
  
  .base.largeScreen & {
    width: 40%;
  }
}

.step {
  width: 8px;
}
</style>
